import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { toast } from "sonner";
import { Plus, X, Check, XCircle, Trash2, Upload, Youtube, Video as VideoIcon, FolderUp, Pencil, Users2, Home, CalendarDays, Handshake, Download } from "lucide-react";
import { compressImageTo100KB } from "../lib/imageCompress";
import { compressVideoTo10MB } from "../lib/videoCompress";
import DestCityFilter from "../components/DestCityFilter";

const CUR_SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const COUNTRIES = ["India", "Nepal", "Sri Lanka", "South Africa", "Uganda", "Nigeria", "Kenya", "Bolivia", "Thailand", "Vietnam"];
const CATEGORIES = ["Cultural", "Adventure", "Culinary", "Wellness", "Nature", "Volunteering", "Nightlife", "Learning", "Shopping", "Other"];

const EMPTY_FORM = {
  name: "", date: "", time: "", venue: "", country: "", city: "", category: "",
  price: 0, cost: 0, currency: "USD", notes: "",
  images: [], video_url: "", youtube_url: "",
};

export default function Activities() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState({ destination: "", city: "" });
  const [editing, setEditing] = useState(null);       // activity object when the edit modal is open
  const [rosterFor, setRosterFor] = useState(null);   // activity object when the roster drawer is open

  const canApprove = ["FOUNDER", "BDOM"].includes(user.effective_role);
  const canDelete = (user.real_role || user.effective_role) === "FOUNDER";

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const { data } = await api.get(`/activities?${q.toString()}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [filter.destination, filter.city]);

  const uploadImages = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const added = [];
    let done = 0;
    const total = files.length;
    for (const f of Array.from(files)) {
      // Skip non-image files silently when the user drags a whole folder.
      if (!f.type?.startsWith("image/")) { done++; continue; }
      try {
        const compressed = await compressImageTo100KB(f);
        const fd = new FormData();
        fd.append("file", compressed, compressed.name || "activity.jpg");
        const { data } = await api.post("/activities/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url);
      } catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || err?.message || "Upload failed"}`); }
      done++;
      if (total > 3) toast.message(`Uploading pictures… ${done}/${total}`, { id: "act-bulk-upload", duration: 1000 });
    }
    if (added.length) { setForm(v => ({ ...v, images: [...v.images, ...added] })); toast.success(`${added.length} picture(s) added`); }
    setBusy(false);
  };

  const uploadVideo = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      let file = f;
      if (f.size > 10 * 1024 * 1024) {
        toast.message(`Compressing ${(f.size/1024/1024).toFixed(1)} MB video…`, { id: "vid-compress", duration: 10000 });
        try {
          file = await compressVideoTo10MB(f, {
            onProgress: (p) => toast.message(`Compressing video… ${Math.round(p * 100)}%`, { id: "vid-compress", duration: 10000 }),
          });
          toast.dismiss("vid-compress");
        } catch (err) {
          toast.dismiss("vid-compress");
          toast.error(`Compression failed: ${err?.message || err}. Please compress manually to ≤10 MB.`);
          setBusy(false); return;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Even after compression, video is ${(file.size/1024/1024).toFixed(1)} MB — please shorten or lower resolution.`);
          setBusy(false); return;
        }
        toast.success(`Compressed to ${(file.size/1024/1024).toFixed(1)} MB`);
      }
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/activities/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(v => ({ ...v, video_url: data.url }));
      toast.success("Video uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Video upload failed"); }
    setBusy(false);
  };

  // Drag-drop for the whole media block. Extracts any image files nested
  // inside a folder drop (webkitGetAsEntry) so PCWM can drag an entire
  // "activity photos" folder in one gesture.
  const [dragOver, setDragOver] = useState(false);
  const handleDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    const items = Array.from(e.dataTransfer?.items || []);
    const files = [];
    const walk = async (entry) => {
      if (!entry) return;
      if (entry.isFile) {
        await new Promise(r => entry.file((f) => { files.push(f); r(); }, r));
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        await new Promise(resolve => {
          const readBatch = () => reader.readEntries(async (entries) => {
            if (!entries.length) return resolve();
            for (const en of entries) await walk(en);
            readBatch();
          });
          readBatch();
        });
      }
    };
    // Try folder walking (webkit API); fall back to plain files list.
    if (items.length && items[0].webkitGetAsEntry) {
      for (const it of items) { const ent = it.webkitGetAsEntry?.(); if (ent) await walk(ent); }
    }
    if (!files.length) {
      for (const f of Array.from(e.dataTransfer?.files || [])) files.push(f);
    }
    if (files.length) uploadImages(files);
  };

  const submit = async () => {
    if (!form.name) return toast.error("Activity name required");
    if (!form.country || !form.city) return toast.error("Country + City required — participants only see activities in their destination");
    await api.post("/activities", { ...form, participants: [] });
    setForm(EMPTY_FORM); setOpen(false); await load(); toast.success("Submitted for approval");
  };
  const approve = async (id, status) => { await api.post(`/activities/${id}/approval`, { status }); await load(); toast.success(status); };
  const del = async (id) => { await api.delete(`/activities/${id}`); await load(); };

  const downloadPdf = async (a) => {
    // Open placeholder tab synchronously so popup blockers stay happy.
    const win = window.open("", "_blank");
    try {
      const res = await fetch(`${API}/activities/${a.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      const link = document.createElement("a");
      link.href = url;
      link.download = `aii-activity-${(a.name || "activity").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      if (win) win.close();
      toast.error(e?.message || "PDF download failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Activity Planner</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <DestCityFilter value={filter} onChange={setFilter} />
          <button onClick={() => setOpen(true)} data-testid="act-new" className="text-sm px-3 py-2 bg-brand text-white rounded-md hover:bg-brand-dark font-semibold flex items-center gap-1.5"><Plus size={14}/> New activity</button>
        </div>
      </div>

      {open && (
        <div className="aii-card p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Plan a new activity</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-1">Once approved, appears on the portal only for participants whose placement matches the chosen country + city.</p>
            </div>
            <button onClick={() => setOpen(false)}><X size={16}/></button>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Activity name *"><input value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} className="input-aii" data-testid="act-name"/></F>
            <F label="Country *">
              <select value={form.country} onChange={e => setForm(v => ({ ...v, country: e.target.value }))} className="input-aii" data-testid="act-country">
                <option value="">— pick —</option>{COUNTRIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="City *"><input value={form.city} onChange={e => setForm(v => ({ ...v, city: e.target.value }))} className="input-aii" placeholder="Jaipur, Kandy, …" data-testid="act-city"/></F>
            <F label="Category">
              <select value={form.category} onChange={e => setForm(v => ({ ...v, category: e.target.value }))} className="input-aii" data-testid="act-category">
                <option value="">— pick —</option>{CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Date"><input type="date" value={form.date} onChange={e => setForm(v => ({ ...v, date: e.target.value }))} className="input-aii"/></F>
            <F label="Time"><input type="time" value={form.time} onChange={e => setForm(v => ({ ...v, time: e.target.value }))} className="input-aii"/></F>
            <F label="Venue"><input value={form.venue} onChange={e => setForm(v => ({ ...v, venue: e.target.value }))} className="input-aii"/></F>
            <F label="Currency">
              <select value={form.currency} onChange={e => setForm(v => ({ ...v, currency: e.target.value }))} className="input-aii">
                {["USD", "EUR", "GBP", "INR"].map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Price per participant"><input type="number" value={form.price} onChange={e => setForm(v => ({ ...v, price: parseFloat(e.target.value) || 0 }))} className="input-aii"/></F>
            <F label="Cost (internal)"><input type="number" value={form.cost} onChange={e => setForm(v => ({ ...v, cost: parseFloat(e.target.value) || 0 }))} className="input-aii"/></F>
          </div>

          {/* Media block */}
          <div className={`border border-dashed rounded-md p-3 space-y-3 transition ${dragOver ? "border-brand bg-[color:var(--aii-green-tint)]/40" : "border-[color:var(--aii-border)]"}`}
               onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
               onDragLeave={() => setDragOver(false)}
               onDrop={handleDrop}
               data-testid="act-media-dropzone">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Media (optional)</div>
              <div className="text-[10px] text-[color:var(--aii-muted)] italic hidden sm:block">
                {dragOver ? "Drop to upload the whole folder" : "Or drag a folder of photos here"}
              </div>
            </div>
            <div className="flex items-center flex-wrap gap-3">
              <label className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded cursor-pointer hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5" data-testid="act-add-pics">
                <Upload size={12}/> {busy ? "Uploading…" : "Add pictures"} <span className="text-[color:var(--aii-muted)]">(auto ≤100 KB each)</span>
                <input type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={e => uploadImages(e.target.files)}/>
              </label>
              <label className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded cursor-pointer hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5" data-testid="act-add-folder"
                     title="Pick an entire folder of pictures">
                <FolderUp size={12}/> Add folder
                <input type="file" multiple className="hidden" disabled={busy}
                       ref={(el) => { if (el) { el.setAttribute("webkitdirectory", ""); el.setAttribute("directory", ""); } }}
                       onChange={e => uploadImages(e.target.files)}/>
              </label>
              <label className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded cursor-pointer hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5" data-testid="act-add-video">
                <VideoIcon size={12}/> {busy ? "Uploading…" : "Add video"} <span className="text-[color:var(--aii-muted)]">(≤10 MB · auto-compressed)</span>
                <input type="file" accept="video/*" className="hidden" disabled={busy} onChange={e => uploadVideo(e.target.files?.[0])}/>
              </label>
              <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
                <Youtube size={14} className="text-red-600"/>
                <input value={form.youtube_url} onChange={e => setForm(v => ({ ...v, youtube_url: e.target.value }))} placeholder="Or paste a YouTube URL" className="input-aii flex-1" data-testid="act-youtube"/>
              </div>
            </div>
            {form.images.length > 0 && (
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                {form.images.map((u, i) => (
                  <div key={`${u}-${i}`} className="relative aspect-square rounded overflow-hidden border border-[color:var(--aii-border)] group">
                    <img src={u} alt="" className="w-full h-full object-cover"/>
                    <button onClick={() => setForm(v => ({ ...v, images: v.images.filter((_, k) => k !== i) }))} className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={10}/></button>
                  </div>
                ))}
              </div>
            )}
            {form.video_url && (
              <div className="text-xs text-[color:var(--aii-muted)] flex items-center gap-2"><VideoIcon size={12}/> Video ready · <button onClick={() => setForm(v => ({ ...v, video_url: "" }))} className="text-red-600 hover:underline">remove</button></div>
            )}
          </div>

          <textarea value={form.notes} onChange={e => setForm(v => ({ ...v, notes: e.target.value }))} rows={2} placeholder="Notes" className="input-aii"/>
          <div className="flex justify-end">
            <button onClick={submit} disabled={busy} data-testid="act-submit" className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark disabled:opacity-60">Submit for approval</button>
          </div>
        </div>
      )}

      <div className="aii-card overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr>
              <th>Activity</th><th>Destination</th><th>City</th><th>Category</th><th>Date</th><th>Venue</th>
              <th className="text-center">Booked</th><th className="text-center">Skipped</th>
              <th>Price</th><th>Revenue</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={12} className="text-center py-10 text-[color:var(--aii-muted)]">No activities yet.</td></tr>}
              {rows.map(a => {
                const booked = a.booked_count || 0;
                const rev = (a.price || 0) * booked;
                return (
                  <tr key={a.id}>
                    <td className="font-medium">{a.name}</td>
                    <td className="text-xs">{a.country || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td className="text-xs">{a.city || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td className="text-xs">{a.category ? <span className="pill" style={{background:"var(--aii-green-tint)", color:"var(--aii-green-dark)"}}>{a.category}</span> : <span className="text-[color:var(--aii-muted)]">—</span>}</td>
                    <td>{a.date || "—"}{a.time ? ` · ${a.time}` : ""}</td>
                    <td>{a.venue || "—"}</td>
                    <td className="text-center">
                      {booked > 0 ? (
                        <button onClick={() => setRosterFor(a)}
                                data-testid={`act-roster-${a.id}`}
                                className="inline-flex flex-col items-center gap-0.5 group cursor-pointer">
                          <span className="pill pill-green group-hover:bg-brand group-hover:text-white transition"
                                title="Open roster">{booked}</span>
                          <span className="text-[10px] text-[color:var(--aii-text-2)] leading-tight max-w-[160px] truncate group-hover:text-brand">
                            {(a.booked_names || []).slice(0, 3).join(", ")}
                            {booked > 3 && <span className="text-brand font-semibold"> · +{booked - 3} more</span>}
                          </span>
                        </button>
                      ) : (
                        <span className="pill pill-green" title="no bookings yet">0</span>
                      )}
                    </td>
                    <td className="text-center"><span className="pill pill-amber">{a.skipped_count || 0}</span></td>
                    <td>{CUR_SYM[a.currency] || a.currency}{(a.price || 0).toLocaleString()}</td>
                    <td className="font-semibold text-brand">{CUR_SYM[a.currency] || a.currency}{rev.toLocaleString()}</td>
                    <td><span className={`pill ${a.status === 'approved' ? 'pill-green' : a.status === 'rejected' ? 'pill-red' : 'pill-amber'}`}>{a.status}</span></td>
                    <td className="text-right">
                      {canApprove && a.status === 'pending' && (
                        <div className="inline-flex gap-1">
                          <button onClick={() => approve(a.id, "approved")} className="text-xs bg-brand text-white px-2 py-1 rounded flex items-center gap-1"><Check size={11}/> Approve</button>
                          <button onClick={() => approve(a.id, "rejected")} className="text-xs bg-red-600 text-white px-2 py-1 rounded flex items-center gap-1"><XCircle size={11}/> Reject</button>
                        </div>
                      )}
                      <button onClick={() => setEditing(a)} data-testid={`act-edit-${a.id}`} className="ml-2 text-[color:var(--aii-muted)] hover:text-brand" title="Edit activity"><Pencil size={14}/></button>
                      {a.status === "approved" && (
                        <button onClick={() => downloadPdf(a)} data-testid={`act-pdf-${a.id}`}
                                className="ml-2 text-[color:var(--aii-muted)] hover:text-brand" title="Download activity PDF">
                          <Download size={14}/>
                        </button>
                      )}
                      {canDelete && <button onClick={() => del(a.id)} className="ml-2 text-[color:var(--aii-muted)] hover:text-red-600"><Trash2 size={14}/></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editing && (
        <EditActivityModal
          activity={editing}
          countries={COUNTRIES}
          categories={CATEGORIES}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      {rosterFor && (
        <RosterDrawer activity={rosterFor} onClose={() => setRosterFor(null)}/>
      )}
    </div>
  );
}

// -------------------------------------------------------------
// RosterDrawer — right-side slide-in that lists each booked
// participant with partner, room and arrival day. Renders
// zero-click ops context off the +N more chip on the planner.
// -------------------------------------------------------------
function RosterDrawer({ activity, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get(`/activities/${activity.id}/roster`);
        if (live) setRows(data);
      } catch { if (live) setRows([]); }
    })();
    return () => { live = false; };
  }, [activity.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={onClose} data-testid="act-roster-drawer">
      <div className="bg-white h-full w-full max-w-md shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold flex items-center gap-1"><Users2 size={11}/> Booked roster</div>
            <h3 className="font-display text-lg font-semibold leading-tight">{activity.name}</h3>
            <div className="text-xs text-[color:var(--aii-muted)]">{activity.date || "—"} · {activity.city || "—"} · {rows ? rows.length : "…"} booked</div>
          </div>
          <button onClick={onClose} className="p-2 text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
        </div>
        <div className="p-4 space-y-2">
          {rows === null && <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">Loading roster…</div>}
          {rows?.length === 0 && <div className="text-sm text-[color:var(--aii-muted)] py-6 text-center">No participants have booked this activity yet.</div>}
          {rows?.map((r, i) => (
            <div key={i} data-testid={`act-roster-row-${i}`}
                 className="border border-[color:var(--aii-border)] rounded-md p-3 hover:border-brand transition">
              <div className="font-semibold text-sm">{r.name || "—"}</div>
              <div className="text-[11px] text-[color:var(--aii-muted)] mt-1 space-y-0.5">
                {r.partner_name && <div className="flex items-center gap-1.5"><Handshake size={11} className="text-brand"/> {r.partner_name}</div>}
                {r.room && <div className="flex items-center gap-1.5"><Home size={11} className="text-brand"/> {r.room}</div>}
                {r.arrival_date && <div className="flex items-center gap-1.5"><CalendarDays size={11} className="text-brand"/> arriving {r.arrival_date}</div>}
                {r.programme && <div className="italic opacity-75">{r.programme}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// EditActivityModal — in-place rename + re-tag + media tweaks.
// Sends only the changed fields to PATCH /api/activities/{id}.
// Approval status is preserved server-side so an approved
// activity stays live during a minor edit.
// -------------------------------------------------------------
function EditActivityModal({ activity, countries, categories, onClose, onSaved }) {
  const [f, setF] = useState({
    name: activity.name || "",
    country: activity.country || "",
    city: activity.city || "",
    category: activity.category || "",
    date: activity.date || "",
    time: activity.time || "",
    venue: activity.venue || "",
    price: activity.price || 0,
    cost: activity.cost || 0,
    currency: activity.currency || "USD",
    notes: activity.notes || "",
    images: activity.images || [],
    video_url: activity.video_url || "",
    youtube_url: activity.youtube_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const addImg = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const added = [];
    for (const file of Array.from(files)) {
      if (!file.type?.startsWith("image/")) continue;
      try {
        const compressed = await compressImageTo100KB(file);
        const fd = new FormData(); fd.append("file", compressed, compressed.name || "activity.jpg");
        const { data } = await api.post("/activities/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url);
      } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Failed"); }
    }
    if (added.length) setF(v => ({ ...v, images: [...v.images, ...added] }));
    setBusy(false);
  };
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.patch(`/activities/${activity.id}`, f);
      toast.success("Activity updated");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="act-edit-modal" onClick={() => !saving && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-2 shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Edit activity</div>
            <h3 className="font-display text-lg font-semibold">{activity.name}</h3>
          </div>
          <button onClick={onClose} disabled={saving} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid sm:grid-cols-3 gap-3">
            <F label="Name *"><input value={f.name} onChange={e => setF(v => ({ ...v, name: e.target.value }))} className="input-aii" data-testid="act-edit-name"/></F>
            <F label="Country">
              <select value={f.country} onChange={e => setF(v => ({ ...v, country: e.target.value }))} className="input-aii">
                <option value="">—</option>{countries.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="City"><input value={f.city} onChange={e => setF(v => ({ ...v, city: e.target.value }))} className="input-aii"/></F>
            <F label="Category">
              <select value={f.category} onChange={e => setF(v => ({ ...v, category: e.target.value }))} className="input-aii" data-testid="act-edit-category">
                <option value="">—</option>{categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Date"><input type="date" value={f.date} onChange={e => setF(v => ({ ...v, date: e.target.value }))} className="input-aii"/></F>
            <F label="Time"><input type="time" value={f.time} onChange={e => setF(v => ({ ...v, time: e.target.value }))} className="input-aii"/></F>
            <F label="Venue"><input value={f.venue} onChange={e => setF(v => ({ ...v, venue: e.target.value }))} className="input-aii"/></F>
            <F label="Currency">
              <select value={f.currency} onChange={e => setF(v => ({ ...v, currency: e.target.value }))} className="input-aii">
                {["USD","EUR","GBP","INR"].map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Price"><input type="number" value={f.price} onChange={e => setF(v => ({ ...v, price: parseFloat(e.target.value) || 0 }))} className="input-aii"/></F>
          </div>
          <F label="Notes"><textarea rows={2} value={f.notes} onChange={e => setF(v => ({ ...v, notes: e.target.value }))} className="input-aii"/></F>
          <div className="border border-dashed border-[color:var(--aii-border)] rounded-md p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Media</div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs px-3 py-1.5 border border-[color:var(--aii-border)] rounded cursor-pointer hover:bg-slate-50 flex items-center gap-1.5">
                <Upload size={12}/> {busy ? "Adding…" : "Add pictures"}
                <input type="file" accept="image/*" multiple className="hidden" disabled={busy} onChange={e => addImg(e.target.files)}/>
              </label>
              <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
                <Youtube size={14} className="text-red-600"/>
                <input value={f.youtube_url} onChange={e => setF(v => ({ ...v, youtube_url: e.target.value }))} placeholder="YouTube URL" className="input-aii flex-1"/>
              </div>
            </div>
            {f.images.length > 0 && (
              <div className="grid grid-cols-6 gap-2">
                {f.images.map((u, i) => (
                  <div key={`${u}-${i}`} className="relative aspect-square rounded overflow-hidden border border-[color:var(--aii-border)] group">
                    <img src={u} alt="" className="w-full h-full object-cover"/>
                    <button onClick={() => setF(v => ({ ...v, images: v.images.filter((_, k) => k !== i) }))} className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"><X size={10}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2 shrink-0">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="act-edit-save"
                  className="px-4 py-2 text-sm bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => (
  <div><label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{label}</label>{children}</div>
);
